import { Box, Checkbox, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { isIPad13, isMobile } from "react-device-detect";
import { ColorScheme } from "../../../Themes/ColorScheme";
import TooltipWrapper from "../../Common/TooltipWrapper";

import { ReactComponent as CheckmarkIcon } from "../../../Assets/Icons/Checkmark.svg";
import { ReactComponent as Ended } from "../../../Assets/Icons/Ended.svg";
import { ReactComponent as EndingSoon } from "../../../Assets/Icons/EndingSoon.svg";
import { ReactComponent as PendingIcon } from "../../../Assets/Icons/Pending.svg";

import { useTranslation } from "react-i18next";
import { ModifyDialog } from "./EditDialog";

function getStatusProperty(status, property) {
	const { t } = useTranslation();

	if (property === "main") {
		switch (status) {
			case 0:
				return ColorScheme.successPrimary;
			case 1:
				return ColorScheme.warningPrimary;
			case 2:
				return ColorScheme.dangerPrimary;
			case 3:
				return ColorScheme.lightGrey;
			default:
				return ColorScheme.lightGrey;
		}
	} else if (property === "secondary") {
		switch (status) {
			case 0:
				return ColorScheme.successSecondary;
			case 1:
				return ColorScheme.warningSecondary;
			case 2:
				return ColorScheme.dangerSecondary;
			case 3:
				return ColorScheme.lightGrey;
			default:
				return ColorScheme.darkGrey;
		}
	} else if (property === "icon") {
		switch (status) {
			case 0:
				return (
					<CheckmarkIcon
						stroke={ColorScheme.successPrimary}
						strokeWidth={2}
						width={"20px"}
						height={"20px"}
					/>
				);
			case 1:
				return (
					<EndingSoon
						stroke={ColorScheme.warningPrimary}
						strokeWidth={2}
						width={"24px"}
						height={"24px"}
					/>
				);
			case 2:
				return (
					<Ended
						stroke={ColorScheme.dangerPrimary}
						strokeWidth={2}
						width={"24px"}
						height={"24px"}
					/>
				);
			case 3:
				return (
					<PendingIcon
						stroke={ColorScheme.darkGrey}
						strokeWidth={2}
						width={"24px"}
						height={"24px"}
					/>
				);
			default:
				return (
					<PendingIcon
						stroke={ColorScheme.darkGrey}
						strokeWidth={2}
						width={"24px"}
						height={"24px"}
					/>
				);
		}
	} else if (property === "text") {
		switch (status) {
			case 0:
				return isMobile ? "" : t("key.ongoing");
			case 1:
				return isMobile ? "" : t("key.ends_soon");
			case 2:
				return isMobile ? "" : t("key.not_valid");
			case 3:
				return isMobile ? "" : t("key.pending");
			default:
				return isMobile ? "" : t("key.ongoing");
		}
	}
}

export const KeyUserList = ({
	entry,
	isLast,
	reservable,
	handleRefresh,
	selectedUsers,
	setSelectedUsers
}) => {
	const [isEmailOverflowing, setIsEmailOverflowing] = useState(false);
	const [isDescriptionOverflowing, setIsDescriptionOverflowing] =
		useState(false);

	const [openDialog, setOpenDialog] = useState(false);
	const [isHovered, setIsHovered] = useState(false);

	const overflowEmail = useRef(null);
	const overflowDescription = useRef(null);
	const { t } = useTranslation();

	const overflowCheck = () => {
		const handleResize = () => {
			if (overflowEmail.current)
				setIsEmailOverflowing(
					overflowEmail.current.offsetWidth <
						overflowEmail.current.scrollWidth
				);

			if (overflowDescription.current)
				setIsDescriptionOverflowing(
					overflowDescription.current.offsetWidth <
						overflowDescription.current.scrollWidth
				);
		};

		window.addEventListener("resize", handleResize);

		return () => window.removeEventListener("resize", handleResize);
	};

	useEffect(() => {
		overflowCheck();
	}, [entry, overflowEmail, overflowDescription]);

	useEffect(() => {
		if (overflowEmail.current)
			setIsEmailOverflowing(
				overflowEmail.current.offsetWidth <
					overflowEmail.current.scrollWidth
			);

		if (overflowDescription.current)
			setIsDescriptionOverflowing(
				overflowDescription.current.offsetWidth <
					overflowDescription.current.scrollWidth
			);
	}, []);

	const handleDialog = (open) => {
		setOpenDialog(open);
	};

	const handleUserSelect = (e) => {
		if (e.target.checked) {
			const user = entry;
			setSelectedUsers((prev) => [...prev, user]);
		} else {
			setSelectedUsers((prev) =>
				prev.filter((user) => user.user_id !== entry.user_id)
			);
		}
	};

	return (
		<div
			key={entry.id}
			style={{
				alignItems: "center",
				borderBottom: !isLast && "1px solid",
				width: "100%",
				backgroundColor: isHovered ? ColorScheme.lightGrey : ColorScheme.transparent,
				cursor: "pointer",
			}}
			onMouseEnter={() => setIsHovered(true)}
    		onMouseLeave={() => setIsHovered(false)}
		>
			<div
			onClick={() => !openDialog && handleDialog(true)}
				style={{
					display: "flex",
					flexDirection: "row",
					justifyContent: "space-between",
					alignItems: "center",
					height: "80px",
					boxSizing: "border-box",
					width: "100%",
					maxWidth: "100%",
				}}
			>
				<div style={{ maxWidth: "50px", width: "50px", minWidth: "50px", display: "flex", alignItems: "center", justifyContent: "center" }}>
					<Checkbox
						onClick={(e) => e.stopPropagation()} // Prevent parent onClick
						checked={selectedUsers.some(user => user.user_id === entry.user_id)}
						onChange={(e) => {
							e.stopPropagation(); // Prevent parent onClick
							handleUserSelect(e, entry.user_id)
						}}
						disableRipple
						sx={{
							color: ColorScheme.primary,             // perus reunus
							borderColor: ColorScheme.lightGrey,
							borderRadius: "4px",
							"&.Mui-checked": {
							color: ColorScheme.primary,
							backgroundColor: ColorScheme.transparent,
							borderColor: ColorScheme.secondary, // pidetään sama reunus
							border: "0px solid"
							},
							"&.MuiCheckbox-root": {
							padding: "0px",
							},
							"&:hover": {
							borderColor: ColorScheme.lightGrey,
							},
						}}
					/>
				</div>
				{isMobile && !isIPad13 ? (
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							minWidth: "calc((100% - 50px) / 2)",
							padding: "10px",
							boxSizing: "border-box"
						}}
					>
						<Typography
							color={ColorScheme.black}
							fontSize="14px"
							fontWeight={700}
						>
							{entry.user_name ? entry.user_name : entry.email}
						</Typography>
						<Typography
							fontSize={10}
							color={ColorScheme.black}
							lineHeight={1.1}
						>
							{new Date(entry.from).getTime() >
							new Date().getTime()
								? new Date(entry.from).toLocaleDateString()
								: ""}
							{new Date(entry.from).getTime() >
								new Date().getTime() && " - "}
							{new Date(entry.end).getFullYear() !== 2038
								? new Date(
										entry.end
									).toLocaleDateString() == "1.1.1"
									? t("key.no_end_date")
									: new Date(
											entry.end
										).toLocaleDateString()
								: t("key.no_end_date")}
						</Typography>

						<Typography
							color={ColorScheme.black}
							fontSize="12px"
							noWrap
							style={{
								textOverflow: "ellipsis",
								overflow: "hidden",
								maxWidth: "100%"
							}}
						>
							{entry.description}
						</Typography>
					</div>
				) : (
					<>
						<div
							style={{
								minWidth: "calc((100% - 50px) / 5)",
								maxWidth: "calc((100% - 50px) / 5)",
								width: "calc((100% - 50px) / 5)",
								paddingLeft: "20px",
								paddingRight: "20px",
								boxSizing: "border-box"
							}}
						>
							<Typography
								color={ColorScheme.black}
								fontSize="16px"
								fontWeight={700}
							>
								{entry.user_name}
							</Typography>

							<TooltipWrapper
								title={
									isEmailOverflowing ? entry.email : undefined
								}
							>
								<Box
									style={{
										flexDirection: "row",
										display: "flex",
										alignItems: "center"
									}}
								>
				
									<Typography
										ref={overflowEmail}
										color={ColorScheme.textSecondary}
										fontSize="12px"
										fontWeight={700}
										noWrap
										style={{
											textOverflow: "ellipsis",
											overflow: "hidden",
											maxWidth: "100%"
										}}
									>
										{entry.email}
									</Typography>
								</Box>
							</TooltipWrapper>
						</div>

						<div
							style={{
								display: "flex",
								width: "calc((100% - 50px) / 5)",
								maxWidth: "calc((100% - 50px) / 5)",
								minWidth: "calc((100% - 50px) / 5)", // <-- Add this line
								height: "100%",
								paddingLeft: "20px",
								paddingRight: "20px",
								boxSizing: "border-box",
								overflow: "hidden",
								alignItems: "center"
							}}
						>
							<TooltipWrapper
								title={
									entry.description
								}
							>
								<Typography
									fontSize={14}
									color={ColorScheme.black}
									noWrap
									style={{
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
										overflow: "hidden",
										maxWidth: "100%"
									}}
								>
									{entry.description}
								</Typography>
							</TooltipWrapper>
						</div>

						<div
							style={{
								minWidth: "calc((100% - 50px) / 5)",
								maxWidth: "calc((100% - 50px) / 5)",
								width: "calc((100% - 50px) / 5)",
								paddingLeft: "20px",
								paddingRight: "20px",
								boxSizing: "border-box"
							}}
						>
							<Typography fontSize={14} color={ColorScheme.black}>
								{entry.phone}
							</Typography>
							<div
								style={{
									flexDirection: "row",
									display: "flex"
								}}
							>
								<Typography fontSize={14} color={ColorScheme.black}>
									{entry.license_plate}
								</Typography>
							</div>
						</div>
						<div
							style={{
								minWidth: "calc((100% - 50px) / 5)",
								maxWidth: "calc((100% - 50px) / 5)",
								width: "calc((100% - 50px) / 5)",
								paddingLeft: "20px",
								paddingRight: "20px",
								boxSizing: "border-box",
								textAlign: "left"
							}}
						>
							<Typography
								fontSize={14}
								color={ColorScheme.black}
								lineHeight={1.1}
							>
								{new Date(entry.from).getTime() >
								new Date().getTime()
									? new Date(entry.from).toLocaleDateString()
									: ""}
								{new Date(entry.from).getTime() >
									new Date().getTime() && " - "}
								{new Date(entry.end).getFullYear() !== 2038
									? new Date(
											entry.end
										).toLocaleDateString() == "1.1.1"
										? t("key.no_end_date")
										: new Date(
												entry.end
											).toLocaleDateString()
									: t("key.no_end_date")}
							</Typography>
						</div>
					</>
				)}

				<div
					style={{
						width: isMobile ? "calc((100% - 50px) / 2)" : "calc((100% - 50px) / 5)",
						display: "flex",
						flexDirection: "row",
						alignItems: "center",
						justifyContent: "flex-end",
						paddingLeft: "20px",
						paddingRight: "20px",
						boxSizing: "border-box"
					}}
				>
					<div
						style={{
							width: "100%",
							display: "flex",
							flexDirection: "row",
							justifyContent: "flex-end",
							position: "relative",
							alignItems: "center"
						}}
					>
						{entry.pin !== null && !entry.pending && (
							<div
								style={{
									backgroundColor:
										entry.pin !== "" &&
										ColorScheme.transparent,
									borderRadius: "4px",
									paddingLeft: "10px",
									paddingRight: "10px",
									paddingTop: "5px",
									paddingBottom: "5px",
									minWidth: "55px",
									textAlign: "center",
									marginRight: "10px",
									zIndex: 0
								}}
							>
								{entry.pin !== "" && (
									<Typography sx={{ fontSize: 12, color: ColorScheme.textSecondary }}>
										{t("doors.pin_code_label")}: {entry.pin}
									</Typography>
								)}
							</div>
						)}

						<div
							style={{
								backgroundColor: getStatusProperty(
									entry.access_status,
									"secondary"
								),
								borderRadius: "20px",
							}}
						>
							<TooltipWrapper
								title={getStatusProperty(
									entry.access_status,
									"text"
								)}
								placement="top"
							>
								<div
									style={{
										minHeight: "28px",
										left: "0px",
										minWidth: "28px",
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										backgroundColor: getStatusProperty(
											entry.access_status,
											"secondary"
										),
										borderRadius: "20px",
									}}
								>
									{getStatusProperty(
										entry.access_status,
										"icon"
									)}
								</div>
							</TooltipWrapper>

							<div style={{ flex: 1 }}></div>
							<div
								style={{
									width: "1px",
									height: "100%",
									backgroundColor: ColorScheme.black
								}}
							></div>
						</div>
							<ModifyDialog
								handleRefresh={handleRefresh}
								reservable={reservable}
								dialogOpen={openDialog}
								closeDialog={handleDialog}
								users={[entry]}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};
